import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Divider,
  TextField,
  Button,
  Stepper,
  Step,
  StepLabel
} from '@mui/material';
import { subscribe, updateUser, changePayment } from '../../actions/user';
import { fetchAccess } from "../../actions/access";
// import { useNavigate } from "react-router-dom";
import { useLocation } from 'react-router-dom';



const Subscribe = () => {
  const dispatch = useDispatch();
  // const navigate = useNavigate();

  const user = useSelector((state) => state.user);
  const access = useSelector((state) => state.access);

  const [activeStep, setActiveStep] = useState(0);
  

  // Local states for profile info
  const [firstName, setFirstName] = useState(user?.customFields?.['first-name'] || '');
  const [lastName, setLastName]   = useState(user?.customFields?.['last-name']  || '');
  const [pharmacyName, setPharmacyName] = useState(user?.customFields?.['pharmacy-name'] || '');
  const [address, setAddress]     = useState(user?.customFields?.['address'] || '');
  const [phone, setPhone]         = useState(user?.customFields?.['phone-number'] || '');
  const [email, setEmail]         = useState(user?.auth?.email || '');
  const [city, setCity]           = useState(user?.customFields?.['city'] || '');
  const [country, setCountry]     = useState('Sri Lanka');

  // Validation errors for step 0
  const [errors, setErrors] = useState({});

  const steps = ['Profile Info', 'Plan & Subscription'];

useEffect(() => {
  if (!user?.id) return;
  dispatch(fetchAccess());
}, [dispatch, user?.id]);


  useEffect(() => {
    setFirstName(user?.firstName ?? user?.customFields?.['first-name'] ?? '');
    setLastName (user?.lastName  ?? user?.customFields?.['last-name']  ?? '');
    setPharmacyName(user?.customFields?.['pharmacy-name'] ?? '');
    setAddress(user?.customFields?.['address'] ?? '');
    setPhone(user?.customFields?.['phone-number'] ?? '');
    setCity(user?.customFields?.['city'] ?? '');
    setEmail(user?.auth?.email ?? user?.email ?? '');
    setCountry(user?.country ?? 'Sri Lanka');
  }, [user]);

const location = useLocation();
const searchParams = new URLSearchParams(location.search);
const mode = searchParams.get('mode');
const plan = access?.subscriptionPlan || 'none';

// PastDue user + /subscribe?mode=change → "change payment" mode
const isChangeMode = plan === 'PastDue' && mode === 'change';

// useEffect(() => {
//   if (!access?.loaded) return;

//   // ACTIVE: never allow subscribe page
//   if (plan === 'Active') {
//     navigate('/dashboard', { replace: true });
//     return;
//   }

//   // PAST DUE: only allow /subscribe?mode=change
//   if (plan === 'PastDue') {
//     if (mode !== 'change') {
//       // user typed /subscribe manually or old link → force to change mode
//       navigate('/subscribe?mode=change', { replace: true });
//     }
//     return; // allow render in change mode
//   }

//   // NONE: only allow normal subscribe, not change mode
//   if (plan === 'none') {
//     if (mode === 'change') {
//       // someone typed /subscribe?mode=change but they don't have a sub
//       navigate('/subscribe', { replace: true });
//     }
//     // otherwise normal /subscribe is allowed
//   }
// }, [access?.loaded, plan, mode, navigate]);


  // ---------- helpers ---------- //

  const validateStep1 = () => {
    const newErrors = {};

    if (!firstName.trim()) newErrors.firstName = 'First name is required.';
    if (!lastName.trim())  newErrors.lastName  = 'Last name is required.';
    if (!pharmacyName.trim()) newErrors.pharmacyName = 'Pharmacy name is required.';
    if (!address.trim())   newErrors.address   = 'Address is required.';
    if (!city.trim())      newErrors.city      = 'City is required.';
    if (!country.trim())   newErrors.country   = 'Country is required.';

    if (!email.trim()) {
      newErrors.email = 'Email is required.';
    } else if (!/^\S+@\S+\.\S+$/.test(email.trim())) {
      newErrors.email = 'Enter a valid email address.';
    }

    if (!phone.trim()) {
      newErrors.phone = 'Phone number is required.';
    } else if (!/^[0-9+\-\s]{7,}$/.test(phone.trim())) {
      newErrors.phone = 'Enter a valid phone number.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // all required fields non-empty (for button disable)
  const isStep1Complete =
    firstName.trim() &&
    lastName.trim() &&
    pharmacyName.trim() &&
    address.trim() &&
    city.trim() &&
    phone.trim() &&
    email.trim() &&
    country.trim();

  // ------------- Handlers ------------- //

  const handleSaveInfo = async () => {
    if (!user?.id) {
      console.error('No user ID found.');
      return;
    }

    // hard block if invalid
    const ok = validateStep1();
    if (!ok) return;

    const updatedData = {
      id: user.id,
      firstName,
      lastName,
      pharmacyName,
      address,
      tel: phone,
      email,
      city,
      country,
    };

    try {
      const result = await dispatch(updateUser(updatedData));
      if (!result?.ok) {
        alert(result?.msg || 'Failed to save info');
        return;
      }
      setActiveStep(1);

    } catch (e) {
      console.error('updateUser failed:', e);
      alert('Failed to save info. Please try again.');
    }
  };

  const handleSubscribe = () => {
    // safety check before hitting PayHere
    if (
      !firstName.trim() ||
      !lastName.trim()  ||
      !email.trim()     ||
      !phone.trim()     ||
      !address.trim()   ||
      !city.trim()      ||
      !country.trim()
    ) {
      // re-run validation so user sees inline errors
      validateStep1();
      alert('Please fill in all required billing fields.');
      return;
    }

    const payload = {
      first_name: firstName.trim(),
      last_name : lastName.trim(),
      email     : email.trim(),
      phone     : phone.trim(),
      address   : address.trim(),
      city      : city.trim(),
      country   : country.trim(),
      userId    : user.id,
    };

    if (isChangeMode) {
    // existing PastDue user: cancel old sub + open checkout via /auth/change-payment-method
    dispatch(changePayment(payload));
  } else {
    // first-time / re-subscribe after full cancel
    dispatch(subscribe(payload));
  }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  // ------------- Render UI ------------- //
  return (
    <Box
      sx={{
        width: { xs: '90%', sm: '50%' },
        margin: { xs: '13%', sm: 'auto' },
        mt: 5,
        mb: 5,
        p: 3,
        border: '1px solid #eaeaea',
        borderRadius: '20px',
      }}
    >
      <Stepper
        activeStep={activeStep}
        alternativeLabel
        sx={{
          mt:2,
          mb: 4,
          '.MuiStepLabel-label': { color: '#1976d2', fontWeight: 500 },
          '.MuiStepIcon-root.Mui-active': { color: '#1976d2' },
          '.MuiStepIcon-root.Mui-completed': { color: '#1976d2' },
        }}
      >
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {activeStep === 0 && (
        <>
          <Typography variant="h5" sx={{ mb: 1 }}>
            Billing Information
          </Typography>
          <Divider sx={{ mb: 3 }} />
          <Typography variant="body1" sx={{ mb: 4 }}>
            These details will be used to contact you regarding billing notifications.
          </Typography>

          <TextField
            fullWidth
            label="First Name"
            variant="outlined"
            required
            sx={{
              mb: 2,
              '& fieldset': { borderColor: '#dadada', borderRadius: '10px' },
              '& .MuiInputBase-input': { padding: '15px 10px' },
            }}
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            error={Boolean(errors.firstName)}
            helperText={errors.firstName}
          />

          <TextField
            fullWidth
            label="Last Name"
            variant="outlined"
            required
            sx={{
              mb: 2,
              '& fieldset': { borderColor: '#dadada', borderRadius: '10px' },
              '& .MuiInputBase-input': { padding: '15px 10px' },
            }}
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            error={Boolean(errors.lastName)}
            helperText={errors.lastName}
          />

          <TextField
            fullWidth
            label="Pharmacy Name"
            variant="outlined"
            required
            sx={{
              mb: 2,
              '& fieldset': {
                borderColor: '#dadada',
                borderRadius: '10px',
              },
              '& .MuiInputBase-input': {
                padding: '15px 10px',
              },
            }}
            value={pharmacyName}
            onChange={(e) => setPharmacyName(e.target.value)}
            error={Boolean(errors.pharmacyName)}
            helperText={errors.pharmacyName}
          />

          <TextField
            fullWidth
            label="Address"
            variant="outlined"
            required
            sx={{
              mb: 2,
              '& fieldset': {
                borderColor: '#dadada',
                borderRadius: '10px',
              },
              '& .MuiInputBase-input': {
                padding: '15px 10px',
              },
            }}
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            error={Boolean(errors.address)}
            helperText={errors.address}
          />

          <TextField
            fullWidth
            label="City"
            variant="outlined"
            required
            sx={{
              mb: 2,
              '& fieldset': { borderColor: '#dadada', borderRadius: '10px' },
              '& .MuiInputBase-input': { padding: '15px 10px' },
            }}
            value={city}
            onChange={(e) => setCity(e.target.value)}
            error={Boolean(errors.city)}
            helperText={errors.city}
          />

          <TextField
            select
            fullWidth
            label="Country"
            variant="outlined"
            required
            sx={{
              mb: 2,
              '& fieldset': { borderColor: '#dadada', borderRadius: '10px' },
              '& .MuiInputBase-input': { padding: '15px 10px' },
            }}
            value={country || 'Sri Lanka'}
            onChange={(e) => setCountry(e.target.value)}
            SelectProps={{ native: true }}
            error={Boolean(errors.country)}
            helperText={errors.country}
          >
            <option value="Sri Lanka">Sri Lanka</option>
          </TextField>

          <TextField
            fullWidth
            label="Phone Number"
            variant="outlined"
            required
            sx={{
              mb: 2,
              '& fieldset': {
                borderColor: '#dadada',
                borderRadius: '10px',
              },
              '& .MuiInputBase-input': {
                padding: '15px 10px',
              },
            }}
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            error={Boolean(errors.phone)}
            helperText={errors.phone}
          />

          <TextField
            fullWidth
            label="Email"
            variant="outlined"
            required
            sx={{
              mb: 2,
              '& fieldset': { borderColor: '#dadada', borderRadius: '10px' },
              '& .MuiInputBase-input': { padding: '15px 10px' },
            }}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={Boolean(errors.email)}
            helperText={errors.email}
          />

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
            <Button
              variant="contained"
              disabled={!isStep1Complete}
              sx={{
                textTransform: 'capitalize',
                boxShadow: 'none',
                borderRadius: '50px',
                padding: '12px 20px',
                backgroundColor: '#1976d2',
                '&:hover': { backgroundColor: '#1565c0' },
              }}
              onClick={handleSaveInfo}
            >
              Save Info
            </Button>
          </Box>
        </>
      )}

      {activeStep === 1 && (
        <>
          <Typography variant="h5" sx={{ mb: 1 }}>
            Plan & Subscription
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Typography variant="body1" sx={{ mb: 2 }}>
            Choose a subscription plan that suits your needs.
          </Typography>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Premium Plan
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • Access to all features
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • Unlimited brand/generic lookups
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • 100 side-effect searches per month
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • Sales, Profit and Stock Reports
            </Typography>
            {/* discount */}
            <Typography variant="h6" sx={{ mt: 2 }}>
              4,500 LKR / month
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button
              variant="outlined"
              sx={{
                textTransform: 'capitalize',
                borderRadius: '50px',
                padding: '12px 20px',
              }}
              onClick={handleBack}
            >
              Back
            </Button>
            <Button
  variant="contained"
  color="primary"
  sx={{
    textTransform: 'capitalize',
    boxShadow: 'none',
    borderRadius: '50px',
    padding: '12px 20px',
  }}
  onClick={handleSubscribe}
>
  {isChangeMode ? 'Confirm' : 'Subscribe'}
</Button>
          </Box>
        </>
      )}
    </Box>
  );
};

export default Subscribe;
